// RESUME SET STATUS **********************************************************************************
// pathname: /v2/resume-operation/${userSlug}/status/${status}
// ****************************************************************************************************
import { useSwrMutationApi } from '@hooks/useSwrApi';
import { SwrToastsProps } from '@type/swr';

// Interface
// ********************************************
interface PayloadData extends ApiSchema<'OperationJob2'> {
  jobSlug?: string;
}
interface SwrResumeSetStatusProps {
  userSlug: string;
  status: string | number;
  locale: string,
  successCallback?: (response?: ApiSchema<'OperationJob'>) => void,
  errorCallback?: (errors?: ApiSchema<'ErrorResponse'>) => void,
  toastsOptions?: SwrToastsProps,
  trackingId?: string,
}

/**
 * @description SWR mutation: Set applicant status.
 * ** Path params **
 * - `userSlug`: The user slug.
 * - `status`: The desired status.
 *
 * ** Props **
 * - `locale`: The locale to use for the request.
 * - `successCallback`: The callback function to call on success.
 * - `errorCallback`: The callback function to call on error.
 * - `toastsOptions`: The SWR toasts options.
 * - `trackingId`: The tracking id for the event.
 */
export const useSwrResumeSetStatus = (props: SwrResumeSetStatusProps) => {
  // Props destructuring
  // ********************************************
  const {
    userSlug,
    status,
    locale,
    successCallback,
    errorCallback,
    toastsOptions,
    trackingId,
  } = props;

  // SWR mutation
  // ********************************************
  const {
    data, error, isMutating, reset, trigger,
  } = useSwrMutationApi<ApiSchema<'OperationJob'>, PayloadData>({
    url: `/v2/resume-operation/${userSlug}/status/${status}`,
    urlParams: trackingId ? `?rid=${trackingId}` : '',
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
    },
    apiOptions: {
      onSuccess: (response) => {
        // Callback
        if (successCallback) successCallback(response);
      },
      onError: (errors) => {
        // Callback
        if (errorCallback) errorCallback(errors);
      },
    },
    toastsOptions: {
      showSuccessToast: true,
      showErrorToast: true,
      ...toastsOptions,
    },
  });

  // Return data
  // ********************************************
  return {
    data,
    error,
    isMutating,
    reset,
    trigger,
  };
};
